import type { BankOnboardingFormValues } from 'src/interfaces/bankOnboarding'
import { EntityIdentificationType, LegalStructure, PersonIdentificationType } from './enums'

export enum OnboardingStep {
  PartnerAndProgramManager = 'partner',
  Business = 'business',
  BusinessAddress = 'business-address',
  BeneficialOwners = 'beneficial-owners',
  ControlPerson = 'control-person',
  Attestor = 'attestor',
  Review = 'review',
}

export const ONBOARDING_STEPS: Array<{ step: OnboardingStep; title: string }> = [
  { step: OnboardingStep.PartnerAndProgramManager, title: 'Partner & program manager' },
  { step: OnboardingStep.Business, title: 'Business' },
  { step: OnboardingStep.BusinessAddress, title: 'Business address' },
  { step: OnboardingStep.BeneficialOwners, title: 'Beneficial owners' },
  { step: OnboardingStep.ControlPerson, title: 'Control person' },
  { step: OnboardingStep.Attestor, title: 'Attestor' },
  { step: OnboardingStep.Review, title: 'Review & submit' },
]

/** The fields each step owns — marked touched when Next fails, so their errors show. */
export const ONBOARDING_STEP_FIELDS: Record<
  OnboardingStep,
  Array<keyof BankOnboardingFormValues>
> = {
  [OnboardingStep.PartnerAndProgramManager]: [
    'partnerId',
    'virtualAccountNickName',
    'programManagerIdentificationNumber',
    'programManagerName',
    'contactName',
    'contactEmailAddress',
  ],
  [OnboardingStep.Business]: [
    'legalName',
    'entityIdentificationType',
    'entityIdentificationNumber',
    'legalStructure',
    'hasAnyDba',
    'dbaName',
    'publiclyTradedStockSymbol',
    'tradedExchange',
    'naicsCode',
    'natureOfBusiness',
    'projectedAnnualRevenue',
    'businessPhoneNumber',
    'countryOfFormation',
    'countryOfPrimaryBusinessOperations',
    'msbServices',
    'cipExempt',
    'hasAnyBeneficialOwner',
    'beneficialOwnerExempt',
    'programParticipantAttestation',
  ],
  [OnboardingStep.BusinessAddress]: [
    'businessAddressLine1',
    'businessAddressLine2',
    'businessCity',
    'businessState',
    'businessZipCode',
    'businessCountry',
  ],
  [OnboardingStep.BeneficialOwners]: ['beneficialOwners'],
  [OnboardingStep.ControlPerson]: [
    'controlPersonTitle',
    'controlPersonFirstName',
    'controlPersonLastName',
    'controlPersonIdentificationType',
    'controlPersonIdentificationNumber',
    'controlPersonIssuingCountry',
    'controlPersonDateOfBirth',
    'controlPersonPhoneNumber',
    'controlPersonAddressLine1',
    'controlPersonAddressLine2',
    'controlPersonCity',
    'controlPersonState',
    'controlPersonZipCode',
    'controlPersonCountry',
  ],
  [OnboardingStep.Attestor]: [
    'attestorFirstName',
    'attestorLastName',
    'attestorJobTitle',
    'dateOfAttestation',
  ],
  [OnboardingStep.Review]: [],
}

/** Plain labels; the CODE is what is sent. NPLAO/NONP/ORG labels to be confirmed by ops. */
export const LEGAL_STRUCTURE_OPTIONS = [
  { value: LegalStructure.Corp, label: 'Corporation (CORP)' },
  { value: LegalStructure.Llc, label: 'Limited liability company (LLC)' },
  { value: LegalStructure.Llcso, label: 'Single-member LLC (LLCSO)' },
  { value: LegalStructure.Part, label: 'Partnership (PART)' },
  { value: LegalStructure.Sp, label: 'Sole proprietorship (SP)' },
  { value: LegalStructure.Nplao, label: 'Non-profit legal entity (NPLAO)' },
  { value: LegalStructure.Nonp, label: 'Non-profit (NONP)' },
  { value: LegalStructure.Org, label: 'Organization (ORG)' },
  { value: LegalStructure.Trust, label: 'Trust (TRUST)' },
  { value: LegalStructure.Estate, label: 'Estate (ESTATE)' },
  { value: LegalStructure.Gov, label: 'Government (GOV)' },
  { value: LegalStructure.Muni, label: 'Municipality (MUNI)' },
]

export const ENTITY_IDENTIFICATION_TYPE_OPTIONS = [
  { value: EntityIdentificationType.Ein, label: 'EIN' },
  { value: EntityIdentificationType.Tin, label: 'TIN' },
]

export const PERSON_IDENTIFICATION_TYPE_OPTIONS = [
  { value: PersonIdentificationType.Ssn, label: 'SSN' },
  { value: PersonIdentificationType.Itin, label: 'ITIN' },
]
